import { ReactNode } from 'react';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { NewRoutingRuleResult } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { LocalizedEnum, lt } from '@mews/framework';
import { IconName } from '@optimus-web/icons';
import { isSubSectionLineObject, LocalizedDateFromUtc, SubSectionLine } from '@mews-commander/ui';
import { TimeService, Typography } from '@optimus-web/core';
import { RoutingRuleDetailProps } from '../../RoutingRuleDetail/RoutingRuleDetail';
import { getTargetDescription, getTargetTitle } from '../../../utils/targetBill';
import { getTimingDescription } from '../../../utils/timing';
import { getRoutingRuleItems, RoutingRuleItemsAdditionalData, RoutingRuleItemsFilters } from '../../../utils/items';
import { getRoutingRuleConditions } from '../../../utils/conditions';
import { RoutingRuleConditionRate } from '../../../types';

const extractName = ({ name }: { id: string; name: string }) => ({ name });

const addAndMapper = (index: number, v: SubSectionLine) =>
    index !== 0 && isSubSectionLineObject(v)
        ? {
              ...v,
              name: (
                  <>
                      {lt('And')} {v.name}
                  </>
              ),
          }
        : v;

export const getRoutingRuleDetailConditions = (routingRuleResult: NewRoutingRuleResult): SubSectionLine[] => {
    const { companies, travelAgencies, rates, prepayment } = getRoutingRuleConditions(routingRuleResult, 0);
    const flattenedRates = rates.flatMap((rate) => rate.rates ?? []);

    return pipe(
        [
            {
                name: lt('RoutingRuleDetailReservationConditionsCompany'),
                compact: true,
                items: [
                    ...companies.map((v) => ({ name: v.name, icon: IconName.Companies })),
                    ...travelAgencies.map((v) => ({ name: v.name, icon: IconName.TravelAgency })),
                ],
            },
            ...(!flattenedRates.length ? [{ name: lt('Rate'), items: [{ name: lt('AllRates') }] }] : []),
            ...(flattenedRates.length
                ? rates.map((rate: RoutingRuleConditionRate) => ({
                      name: (
                          <>
                              {rate.serviceName} {lt('Rate')}
                          </>
                      ),
                      compact: true,
                      items: rate.rates.length ? rate.rates.map(extractName) : [{ name: lt('AllRates') }],
                  }))
                : []),
        ],
        array.filter((v) => v.items.length > 0),
        (arr) =>
            prepayment
                ? array.concat([
                      {
                          name: lt('RoutingRuleDetailReservationConditionsPrepaidInOnlineTravelAgency'),
                      },
                  ])(arr)
                : arr,
        array.mapWithIndex(addAndMapper)
    );
};

const getFooter = (routingRuleResult: NewRoutingRuleResult): ReactNode => {
    const routingRule = routingRuleResult.routingRules?.[0];
    if (!routingRule) {
        return '';
    }

    const creator = routingRuleResult.profiles?.find((v) => v.id === routingRule.creatorProfileId);

    return (
        <Typography textStyle="bodySmall" variant="tertiary" as="div">
            {lt('RoutingRuleDetailCreation', {
                user: creator?.name || '',
                date: <LocalizedDateFromUtc utcString={routingRule.createdUtc} />,
            })}
        </Typography>
    );
};

const DEFAULT_ROUTING_RULE_ITEMS_FILTERS: RoutingRuleItemsFilters = {
    serviceIds: [],
    productIds: [],
};

interface MapRoutingRuleToRoutingRuleDetailParams {
    routingRuleResult: NewRoutingRuleResult | undefined;
    additionalData: RoutingRuleItemsAdditionalData;
    timeService: TimeService;
    filters?: RoutingRuleItemsFilters;
    aggregateItems?: boolean;
}

export const mapRoutingRuleToRoutingRuleDetail = ({
    routingRuleResult,
    additionalData,
    timeService,
    filters = DEFAULT_ROUTING_RULE_ITEMS_FILTERS,
    aggregateItems,
}: MapRoutingRuleToRoutingRuleDetailParams): Pick<
    RoutingRuleDetailProps,
    'title' | 'description' | 'footer' | 'sections'
> & { isDeleted: boolean } => {
    if (!routingRuleResult?.routingRules?.length) {
        return { title: '', description: '', sections: [], isDeleted: false };
    }

    const routingRule = routingRuleResult.routingRules[0];

    const timingDescription = getTimingDescription({
        triggerType: routingRule.triggerType,
        processingStartOffset: routingRule.processingStartOffset,
        timeService,
        withContinuousDescription: true,
    });

    const targetDescription = getTargetDescription(routingRule.assignmentTargetType, routingRule.billAggregationType);

    return {
        title: routingRule.name || '',
        description: routingRule.description || '',
        isDeleted: routingRule.isDeleted,
        footer: routingRule.isDeleted ? null : getFooter(routingRuleResult),
        sections: [
            {
                id: 'timing-section',
                subSections: [
                    {
                        id: 'timing-section-1',
                        lines: [
                            {
                                id: TextKey.Timing,
                                icon: IconName.Time,
                                name: lt(TextKey.Timing),
                                strong: true,
                                items: [
                                    {
                                        name: (
                                            <span data-test-id={routingRule.triggerType}>
                                                <LocalizedEnum
                                                    value={routingRule.triggerType}
                                                    prefix="RoutingRuleTriggerType"
                                                />
                                            </span>
                                        ),
                                    },
                                ],
                            },
                            ...(timingDescription ? [timingDescription] : []),
                        ],
                    },
                ],
            },
            {
                id: 'reservation-conditions-section',
                subSections: [
                    {
                        id: 'reservation-conditions-section-1',
                        lines: [
                            {
                                id: TextKey.RoutingRuleDetailReservationConditions,
                                icon: IconName.TopManagement,
                                name: lt(TextKey.RoutingRuleDetailReservationConditions),
                                strong: true,
                            },
                            ...getRoutingRuleDetailConditions(routingRuleResult),
                        ],
                    },
                ],
            },
            {
                id: 'items-section',
                subSections: [
                    {
                        id: 'items-section-1',
                        lines: [
                            {
                                id: TextKey.RoutingRuleDetailItems,
                                icon: IconName.Items,
                                name: lt(TextKey.RoutingRuleDetailItems),
                                strong: true,
                            },
                            ...getRoutingRuleItems({
                                routingRuleResult,
                                additionalData,
                                index: 0,
                                filters,
                                aggregated: aggregateItems,
                            }).map((item) => ({
                                id: item.assignmentId,
                                name: item.serviceName,
                                compact: !aggregateItems,
                                items: item.items.map((v) => ({ name: v })),
                            })),
                        ],
                    },
                ],
            },
            {
                id: 'destination-section',
                subSections: [
                    {
                        id: 'destination-section-1',
                        lines: [
                            {
                                id: TextKey.TargetBill,
                                icon: IconName.BillsInvoices,
                                name: lt(TextKey.TargetBill),
                                strong: true,
                                items: [{ name: getTargetTitle(routingRule.assignmentTargetType) }],
                            },
                            ...(targetDescription ? [targetDescription] : []),
                        ],
                    },
                ],
            },
        ],
    };
};
